import { CommonPrivacy, CommonSharingScope } from 'features/common'
import {
  BigQueryConfigType,
  Connection,
  ConnectionParams,
  DatabricksRestParams,
  ProviderType,
  SnowflakeConfigType,
  ConnectionLocation
} from 'features/connections/types'

export const DEFAULT_TEMP_LOCATION_NAME = 'carto_temp'
export const DEFAULT_WORKSPACE_LOCATION_NAME = 'carto_workspace'
export const DEFAULT_EXTENSIONS_LOCATION_NAME = 'carto_extensions'

export const prepareBQConnectionPayload = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { data }: { data: any },
  connection?: Connection
) => {
  const {
    name,
    privacy,
    billing_project,
    serviceAccount,
    groups,
    sharingScope,
    namedSourcesOnly,
    viewerCredentials,
    atLocation,
    doLocation,
    tempLocations,
    workspaceLocations,
    extensionsLocations,
    maxConcurrentQueries,
    queryTimeoutS,
    dataTransferVersionInfo,
    connectionType,
    serviceAccountEmail,
    ...credentials
  } = data
  const config = {
    type: connectionType,
    billing_project,
    credentials:
      connectionType === BigQueryConfigType.WorkloadIdentity
        ? { serviceAccountEmail: serviceAccountEmail === '' ? undefined : serviceAccountEmail }
        : serviceAccount
        ? (JSON.parse(serviceAccount) as ConnectionParams)
        : credentials,
    dataTransferVersionInfo
  }
  const newSharingScope = prepareSharingScope(data.privacy, sharingScope)
  const newConnection: Connection = {
    name,
    privacy,
    spatial_extension: true,
    provider_id: ProviderType.BIGQUERY,
    groups,
    config,
    viewerCredentials,
    namedSourcesOnly,
    id: '',
    doLocation,
    atLocation,
    sharingScope: newSharingScope,
    tempLocations:
      connection && buildConnectionLocations(tempLocations || DEFAULT_TEMP_LOCATION_NAME, connection?.tempLocations),
    workspaceLocations:
      connection &&
      buildConnectionLocations(workspaceLocations || DEFAULT_WORKSPACE_LOCATION_NAME, connection?.workspaceLocations),
    extensionsLocations:
      connection &&
      buildConnectionLocations(
        extensionsLocations || DEFAULT_EXTENSIONS_LOCATION_NAME,
        connection?.extensionsLocations
      ),
    maxConcurrentQueries: maxConcurrentQueries === '' ? null : Number(maxConcurrentQueries),
    queryTimeoutS: queryTimeoutS === '' ? null : Number(queryTimeoutS)
  }
  return newConnection
}

export const prepareConnectionPayload = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { data, provider }: { data: any; provider: ProviderType },
  connection?: Connection
) => {
  const {
    name,
    privacy,
    spatial_extension,
    groups,
    viewerCredentials,
    tempLocations,
    workspaceLocations,
    extensionsLocations,
    atLocation,
    doLocation,
    external_idp_id,
    maxConcurrentQueries,
    queryTimeoutS,
    sharingScope,
    namedSourcesOnly,
    connectionType: type,
    ...credentials
  } = data

  const isEditing = !!connection?.id

  const cleanCredentials = Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(credentials).filter(([_, value]) => value !== '')
  ) as unknown as ConnectionParams
  const config = { type, credentials: cleanCredentials }

  if (
    type === SnowflakeConfigType.KeyPair &&
    isEditing &&
    cleanCredentials.privateKey?.includes('Private key ending')
  ) {
    delete cleanCredentials.privateKey
    delete cleanCredentials.passphrase
  }
  if (type === 'oauth_m2m' && isEditing && data.servicePrincipalSecret === '••••••••') {
    delete (cleanCredentials as DatabricksRestParams).servicePrincipalSecret
  }

  if (type === 'oauth_u2m') {
    delete (cleanCredentials as DatabricksRestParams).host
    if (isEditing && !data.state) {
      delete (cleanCredentials as DatabricksRestParams).userIdentifier
      delete (cleanCredentials as DatabricksRestParams).userEmail
    }
  }

  const newSharingScope = prepareSharingScope(data.privacy, sharingScope)
  const newConnection: Connection = {
    name,
    privacy,
    spatial_extension,
    provider_id: provider,
    config,
    groups,
    sharingScope: newSharingScope,
    namedSourcesOnly,
    viewerCredentials,
    id: '',
    external_idp_id,
    tempLocations:
      connection && buildConnectionLocations(tempLocations || DEFAULT_TEMP_LOCATION_NAME, connection?.tempLocations),
    workspaceLocations:
      connection &&
      buildConnectionLocations(workspaceLocations || DEFAULT_WORKSPACE_LOCATION_NAME, connection?.workspaceLocations),
    extensionsLocations:
      connection &&
      buildConnectionLocations(
        extensionsLocations || DEFAULT_EXTENSIONS_LOCATION_NAME,
        connection?.extensionsLocations
      ),
    atLocation,
    doLocation,
    maxConcurrentQueries: maxConcurrentQueries === '' ? null : Number(maxConcurrentQueries),
    queryTimeoutS: queryTimeoutS === '' ? null : Number(queryTimeoutS)
  }
  return newConnection
}

const buildConnectionLocations = (location: string, connectionLocation?: ConnectionLocation[]) => {
  const connectionLocations: ConnectionLocation[] = []

  if (connectionLocation?.length) {
    for (let i = 0; i < connectionLocation.length; i++) {
      // Edit the name of the first workflowsTemp
      connectionLocations.push({
        id: connectionLocation[i]?.id,
        name: i === 0 ? location : connectionLocation[i]?.name
      } as ConnectionLocation)
    }
  } else {
    // Create the first workflowsTemp
    connectionLocations.push({
      name: location
    } as ConnectionLocation)
  }
  return connectionLocations
}

const prepareSharingScope = (privacy?: CommonPrivacy, sharingScope?: CommonSharingScope) => {
  if (privacy === CommonPrivacy.Private) {
    return undefined
  }
  return sharingScope
}
